// Arranjo dos rótulos (nomes e balões) em cima dos personagens, sem sobreposição.
//
// Quem importa mais é posicionado primeiro (esperando você, selecionado, e depois quem
// está mais à frente na tela). Um rótulo que bateria em outro sobe um degrau; se não
// couber em MAX_STEPS degraus, some — melhor faltar um nome do que virar borrão.
// Também aqui os textos do balão de fala e do cartão ao passar o mouse (puros, testáveis em Node):
// o campus só desenha o que eles devolvem.
import type { AgentStatus } from '../../core/types.js';
import { type Chave, duracaoTurno, iconeDoKind, t, tActivity, tempoRelativo } from '../i18n.js';

export interface LabelBox {
  id: string;
  /** Centro desejado. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Maior = posicionado antes (ganha o lugar). */
  priority: number;
  /** Nunca esconde (esperando você, selecionado). */
  keep?: boolean;
}

export interface PlacedLabel extends LabelBox {
  /** Quanto subiu em relação ao lugar desejado (px de tela). */
  dy: number;
}

const MAX_STEPS = 3;
const MARGIN = 2;

function overlaps(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }): boolean {
  return Math.abs(a.x - b.x) * 2 < a.w + b.w + MARGIN * 2 && Math.abs(a.y - b.y) * 2 < a.h + b.h + MARGIN * 2;
}

export function arrangeLabels(boxes: readonly LabelBox[]): PlacedLabel[] {
  const order = [...boxes].sort((a, b) => b.priority - a.priority || b.y - a.y);
  const placed: PlacedLabel[] = [];
  for (const box of order) {
    let done = false;
    for (let step = 0; step <= MAX_STEPS; step++) {
      const dy = step === 0 ? 0 : -step * (box.h + MARGIN);
      const candidate = { ...box, y: box.y + dy, dy };
      if (!placed.some((p) => overlaps(p, candidate))) {
        placed.push(candidate);
        done = true;
        break;
      }
    }
    if (!done && box.keep) placed.push({ ...box, dy: 0 });
  }
  return placed;
}

// ------------------------------------------------------------------ textos do balão e do cartão

/** Atividade como o balão e o cartão a leem (o Activity do servidor, sem o id). */
export interface AtividadeVista {
  kind: string;
  text: string;
  detail?: string;
}

/** Tamanho máximo do balão, em pontos de código: o resto some (o cartão mostra a linha inteira). */
const BALAO_MAX = 40;

/** Corta em `max` pontos de código (um emoji conta como um), com reticências. */
function corta(texto: string, max: number): string {
  const pontos = [...texto];
  return pontos.length > max ? `${pontos.slice(0, max - 1).join('')}…` : texto;
}

/**
 * A atividade numa linha, com ícone: '📖 Lendo carrinho.ts'. 'done' vira 'Concluiu em 22s' quando
 * o agente tem a duração do último turno (ela vem do servidor, em Agent.lastTurnMs).
 */
export function linhaDaAtividade(atividade: AtividadeVista, concluiuEm?: number): string {
  if (atividade.kind === 'done' && concluiuEm !== undefined) return t('det.concluiu', { duracao: duracaoTurno(concluiuEm) });
  const texto = tActivity(atividade.text);
  return `${iconeDoKind(atividade.kind, atividade.text, atividade.detail)} ${texto.charAt(0).toUpperCase()}${texto.slice(1)}`;
}

export interface BalaoVisto {
  /** Levantou a mão (status waiting ou pedido de aprovação pendente). */
  aguardando: boolean;
  /** Status 'thinking' sem atividade para mostrar. */
  pensando: boolean;
  /** Atividade que ainda está no tempo do balão (quem chama já filtrou o tempo). */
  atividade?: AtividadeVista;
  concluiuEm?: number;
  /** Fala que não é atividade: vida social ou conversa de ocioso (já com emoji). */
  fala?: string;
}

/** Texto do balão acima do personagem: '✋ Esperando você', '✅ Concluiu em 22s'… ou '' se não há nada a dizer. */
export function textoDoBalao(visto: BalaoVisto): string {
  if (visto.aguardando) return `✋ ${t('balao.esperando')}`;
  if (visto.atividade) return corta(linhaDaAtividade(visto.atividade, visto.concluiuEm), BALAO_MAX);
  if (visto.fala) return visto.fala;
  return visto.pensando ? '…' : '';
}

export interface CartaoVisto {
  nome: string;
  subagente: boolean;
  /** Título da sessão quando há; senão a pasta do projeto. */
  projeto: string;
  status: AgentStatus;
  /** Desde quando está no status atual (epoch ms); só ao vivo. */
  statusDesde?: number;
  /** Última atividade conhecida do agente. */
  atividade?: AtividadeVista;
  concluiuEm?: number;
  /** Agora (epoch ms), para o 'há X' ser testável; padrão: Date.now(). */
  agora?: number;
}

export interface CartaoConteudo {
  nome: string;
  projeto: string;
  /** Rótulo do status: 'trabalhando', 'ocioso'… */
  status: string;
  /** 'há 3 min' desde o início do status; '' quando não se sabe. */
  ha: string;
  /** Última atividade numa linha com ícone; '' quando não há. */
  atual: string;
}

const ROTULO_DO_STATUS: Record<AgentStatus, Chave> = {
  working: 'status.working',
  thinking: 'status.thinking',
  waiting: 'status.waiting',
  shell: 'status.shell',
  idle: 'status.idle',
};

/** O que o cartão ao passar o mouse mostra, já como texto (quem desenha escapa e pinta). */
export function conteudoDoCartao(visto: CartaoVisto): CartaoConteudo {
  return {
    nome: visto.subagente ? `${visto.nome} · ${t('cartao.subagente')}` : visto.nome,
    projeto: visto.projeto,
    status: t(ROTULO_DO_STATUS[visto.status]),
    ha: visto.statusDesde !== undefined ? tempoRelativo(visto.statusDesde, visto.agora) : '',
    atual: visto.atividade ? linhaDaAtividade(visto.atividade, visto.concluiuEm) : '',
  };
}
